import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

const formSchema = z.object({
    first_name: z.string().trim().min(1, 'First name is required'),
    last_name: z.string().trim().min(1, 'Last name is required'),
    preferred_name: z.string().optional(),
    address_line_1: z.string().trim().min(1, 'Address line 1 is required'),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    phone_number: z.string().optional(),
    email: z
        .string()
        .email('Invalid email address')
        .optional()
        .or(z.literal('')),
    care_plan: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type CreateClientModalProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationId: string;
    readonly onClientCreated: (client: {
        id: string;
        first_name: string;
        last_name: string;
        preferred_name?: string | null;
        address_line_1?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        care_plan?: string | null;
    }) => void;
};

const DEFAULT_VALUES: FormValues = {
    first_name: '',
    last_name: '',
    preferred_name: '',
    address_line_1: '',
    city: '',
    state: '',
    postal_code: '',
    phone_number: '',
    email: '',
    care_plan: '',
};

const toNullableTrimmed = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
};

export function CreateClientModal({
    open,
    onOpenChange,
    organizationId,
    onClientCreated,
}: CreateClientModalProps) {
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: DEFAULT_VALUES,
    });

    useEffect(() => {
        if (open) {
            form.reset(DEFAULT_VALUES);
        }
    }, [open, form]);

    const onSubmit = async (values: FormValues) => {
        if (!organizationId) {
            toast.error('Cannot create client', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
            return;
        }

        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/clients',
                pathParams: { organization_id: organizationId },
                body: {
                    first_name: values.first_name.trim(),
                    last_name: values.last_name.trim(),
                    preferred_name: toNullableTrimmed(values.preferred_name),
                    address_line_1: values.address_line_1.trim(),
                    city: toNullableTrimmed(values.city),
                    state: toNullableTrimmed(values.state),
                    postal_code: toNullableTrimmed(values.postal_code),
                    phone_number: toNullableTrimmed(values.phone_number),
                    email: toNullableTrimmed(values.email),
                    care_plan: toNullableTrimmed(values.care_plan),
                    is_active: true,
                },
            });

            onClientCreated({
                id: response.id,
                first_name: response.first_name,
                last_name: response.last_name,
                preferred_name: response.preferred_name,
                address_line_1: response.address_line_1,
                city: response.city,
                state: response.state,
                postal_code: response.postal_code,
                care_plan: response.care_plan,
            });
            toast.success('Client created', { persistNotification: false });
            onOpenChange(false);
            form.reset(DEFAULT_VALUES);
        } catch (error) {
            console.error('Failed to create client:', error);
            toast.error('Failed to create client', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Create Client</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        <p className="text-muted-foreground text-xs">
                            Fields marked with * are required.
                        </p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="first_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First name *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="First name"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="last_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last name *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Last name"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="preferred_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preferred name</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Preferred name"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="phone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Phone number</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="tel"
                                                placeholder="Phone number"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="sm:col-span-2">
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder="Email"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="address_line_1"
                                render={({ field }) => (
                                    <FormItem className="sm:col-span-2">
                                        <FormLabel>Address line 1 *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Street address"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <FormField
                                control={form.control}
                                name="city"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>City</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="City"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="state"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>State</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="State"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="postal_code"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Postal code</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Postal code"
                                                {...field}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <FormField
                            control={form.control}
                            name="care_plan"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Care plan</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            rows={6}
                                            placeholder="Care plan"
                                            {...field}
                                            disabled={
                                                form.formState.isSubmitting
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={form.formState.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={form.formState.isSubmitting}
                            >
                                Create client
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
